import { useState, useEffect } from 'react';
import { Save, Settings, Zap, Shield } from 'lucide-react';
import Modal from '../common/Modal';
import type { RoutingPolicy, PolicyCreateData } from '../../services';

interface PolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PolicyCreateData) => void;
  policy?: RoutingPolicy | null;
  mode: 'add' | 'edit';
}

const PolicyModal = ({ isOpen, onClose, onSave, policy, mode }: PolicyModalProps) => {
  const [formData, setFormData] = useState<PolicyCreateData>({
    name: '',
    description: '',
    priority: 100,
    enabled: 1,
    scope: 'global',
    inspection_mode: 'auto',
    default_action: 'allow',
  });

  useEffect(() => {
    if (isOpen) {
      if (policy) {
        setFormData({
          name: policy.name || '',
          description: policy.description || '',
          priority: policy.priority,
          enabled: policy.enabled,
          scope: (policy.scope === 'device_group' || policy.scope === 'dg_template') ? policy.scope : 'global',
          inspection_mode: policy.inspection_mode || 'auto',
          default_action: policy.default_action || 'allow',
        });
      } else {
        setFormData({
          name: '',
          description: '',
          priority: 100,
          enabled: 1,
          scope: 'global',
          inspection_mode: 'auto',
          default_action: 'allow',
        });
      }
    }
  }, [isOpen, policy]);

  const handleChange = (field: keyof PolicyCreateData, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // When switching to DG scope, default to deny (Zero Trust) and force pre_inspection
      if (field === 'scope' && (value === 'device_group' || value === 'dg_template')) {
        if (!policy) next.default_action = 'deny';
        next.inspection_mode = 'pre_inspection';
      }
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Force pre_inspection for DG scope
    const submitData = { ...formData };
    if (submitData.scope === 'device_group' || submitData.scope === 'dg_template') {
      submitData.inspection_mode = 'pre_inspection';
    }
    onSave(submitData);
  };

  // Scope is immutable after creation
  const scopeDisabled = mode === 'edit';
  const isDgScope = formData.scope === 'device_group' || formData.scope === 'dg_template';

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button type="button" onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button form="policy-form" type="submit" className="btn-primary inline-flex items-center space-x-2">
        <Save className="w-4 h-4" />
        <span>{mode === 'add' ? 'Create Policy' : 'Save Changes'}</span>
      </button>
    </div>
  );

  const scopeLabel = formData.scope === 'dg_template' ? 'DG Access Template'
    : formData.scope === 'device_group' ? 'DG Access Policy'
    : 'Policy Template';
  const modalTitle = mode === 'add'
    ? `Create ${scopeLabel}`
    : `Edit ${scopeLabel}: ${formData.name}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="lg"
      footer={footer}
    >
      <form id="policy-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Scope Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Policy Scope</label>
          <select
            value={formData.scope || 'global'}
            onChange={(e) => handleChange('scope', e.target.value as 'global' | 'device_group' | 'dg_template')}
            className="input-base w-full"
            disabled={scopeDisabled}
          >
            <option value="global">Global Template</option>
            <option value="dg_template">Device Group Access Template</option>
            {/* device_group is only created via template apply — show in edit mode only */}
            {mode === 'edit' && formData.scope === 'device_group' && (
              <option value="device_group">Device Group Access Policy (Applied)</option>
            )}
          </select>
          <p className="text-xs text-gray-500">
            {formData.scope === 'dg_template'
              ? 'Access templates define rules for device group members. Apply a template to a device group to create an edge-specific policy with real subnet values.'
              : formData.scope === 'device_group'
              ? 'This applied policy was created from a template. Rules use edge-specific subnet values.'
              : 'Global Templates can be applied (cloned) to specific edges for deployment.'
            }
          </p>
          {scopeDisabled && (
            <p className="text-xs text-amber-600">Scope cannot be changed after creation.</p>
          )}
        </div>

        {/* Basic Information */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Policy Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Settings className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., VoIP Priority Routing"
              className="input-base w-full pl-10"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Describe the purpose of this routing policy..."
            className="input-base w-full resize-none"
            rows={3}
          />
        </div>

        {/* Policy Settings */}
        <div className="border-t border-gray-100 pt-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
            <Zap className="w-4 h-4 mr-2 text-yellow-500" />
            Policy Settings
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Priority</label>
              <input
                type="number"
                value={formData.priority}
                onChange={(e) => handleChange('priority', parseInt(e.target.value))}
                placeholder="100"
                className="input-base w-full"
                min="1"
                max="1000"
                required
              />
              <p className="text-xs text-gray-500">
                Lower values = higher priority (1-1000)
              </p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={formData.enabled}
                onChange={(e) => handleChange('enabled', parseInt(e.target.value))}
                className="input-base w-full"
              >
                <option value="1">Enabled</option>
                <option value="0">Disabled</option>
              </select>
            </div>

            {/* Default Action — DG scope only */}
            {isDgScope && (
              <div className="space-y-2 col-span-2">
                <label className="block text-sm font-medium text-gray-700 flex items-center">
                  <Shield className="w-4 h-4 mr-1 text-gray-400" />
                  Default Action
                </label>
                <select
                  value={formData.default_action || 'allow'}
                  onChange={(e) => handleChange('default_action', e.target.value)}
                  className="input-base w-full"
                >
                  <option value="deny">Deny by default (Zero Trust) — Recommended</option>
                  <option value="allow">Allow by default — only listed traffic is blocked</option>
                </select>
                <p className="text-xs text-gray-500">
                  {formData.default_action === 'deny'
                    ? 'All traffic from this group is blocked unless explicitly allowed by a rule. Add "allow" rules to whitelist destinations.'
                    : 'All traffic from this group is permitted. Add "drop" or "rate limit" rules to restrict specific destinations.'}
                </p>
              </div>
            )}

            {/* Inspection Mode — hidden for DG scope (L3/L4 only) */}
            {!isDgScope && (
              <div className="space-y-2 col-span-2">
                <label className="block text-sm font-medium text-gray-700">Inspection Mode</label>
                <select
                  value={formData.inspection_mode || 'auto'}
                  onChange={(e) => handleChange('inspection_mode', e.target.value)}
                  className="input-base w-full"
                >
                  <option value="auto">Auto (Recommended)</option>
                  <option value="pre_inspection">Fast Path (L3/L4 only, no Suricata)</option>
                  <option value="post_inspection">Full Inspection (all rules via Suricata)</option>
                </select>
                <p className="text-xs text-gray-500">
                  {(formData.inspection_mode || 'auto') === 'auto' && 'Rules are auto-classified based on match criteria'}
                  {formData.inspection_mode === 'pre_inspection' && 'Bypasses Suricata for maximum performance'}
                  {formData.inspection_mode === 'post_inspection' && 'Forces all traffic through Suricata inspection'}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <p className="text-sm text-blue-700">
            {formData.scope === 'dg_template' ? (
              <>
                <strong>Note:</strong> After creating, add access rules with placeholder destinations. When applying this template to a device group, you'll select real edge subnets for each rule.
                {formData.default_action === 'deny'
                  ? ' Traffic not matched by any rule is blocked.'
                  : ' Traffic not matched by any rule is allowed.'}
              </>
            ) : formData.scope === 'device_group' ? (
              <>
                <strong>Note:</strong> This is an applied policy with edge-specific subnet values. Edit rules to update access control for the linked device group.
                {formData.default_action === 'deny'
                  ? ' Traffic not matched by any rule is blocked.'
                  : ' Traffic not matched by any rule is allowed.'}
              </>
            ) : (
              <>
                <strong>Note:</strong> After creating the template, you can add routing rules to define how traffic matching specific applications should be handled. Templates can then be applied to specific edges.
              </>
            )}
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default PolicyModal;
